import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSubscriptions } from '@/hooks/useSubscriptions';

const methodColor: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-600',
  POST: 'bg-brand-50 text-brand-600',
  PUT: 'bg-amber-50 text-amber-600',
  DELETE: 'bg-red-50 text-red-500',
  PATCH: 'bg-violet-50 text-violet-600',
};

interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  description: string;
  params?: { name: string; type: string; required: boolean; description: string }[];
  body?: { name: string; type: string; required: boolean; description: string }[];
  response_example: string;
}

interface ApiDoc {
  product_id: string;
  product_name: string;
  base_url: string;
  version: string;
  endpoints: ApiEndpoint[];
}

function buildApiDoc(productName: string, productId: string): ApiDoc {
  const slug = productName.toLowerCase().replace(/\s+/g, '');
  return {
    product_id: productId,
    product_name: productName,
    base_url: `https://api.apimarts.com/v1/${slug}`,
    version: 'v1.0.0',
    endpoints: [
      {
        method: 'GET',
        path: '/status',
        description: `Check the health and status of the ${productName}`,
        response_example: `{\n  "status": "operational",\n  "version": "v1.0.0",\n  "uptime": "99.97%",\n  "timestamp": "${new Date().toISOString()}"\n}`,
      },
      {
        method: 'POST',
        path: '/query',
        description: `Submit a query to the ${productName}`,
        body: [
          { name: 'input', type: 'string', required: true, description: 'The input data to process' },
          { name: 'options', type: 'object', required: false, description: 'Optional configuration parameters' },
        ],
        response_example: `{\n  "id": "req_${Math.random().toString(36).slice(2, 10)}",\n  "status": "success",\n  "result": {},\n  "latency_ms": 124\n}`,
      },
      {
        method: 'GET',
        path: '/results/{id}',
        description: 'Retrieve a previous result by ID',
        params: [
          { name: 'id', type: 'string', required: true, description: 'The result ID returned from a query' },
        ],
        response_example: `{\n  "id": "req_abc123",\n  "status": "completed",\n  "result": {},\n  "created_at": "${new Date().toISOString()}"\n}`,
      },
    ],
  };
}

export default function ApiDocs() {
  const { subscriptions, loading } = useSubscriptions();
  const activeApis = subscriptions.filter(s => s.status === 'active');

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [selectedEndpoint, setSelectedEndpoint] = useState(0);
  const [copiedCode, setCopiedCode] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-gray-400 text-2xl" />
      </div>
    );
  }

  if (activeApis.length === 0) {
    return (
      <div className="bg-white rounded-xl p-16 text-center">
        <i className="ri-file-code-line text-4xl text-gray-200 block mb-4" />
        <h3 className="font-semibold text-gray-700 mb-2">No active subscriptions</h3>
        <p className="text-sm text-gray-400">Purchase an API from the marketplace to access documentation.</p>
      </div>
    );
  }

  const selected = activeApis[selectedIdx] ?? activeApis[0];
  const doc = buildApiDoc(selected.product_name, selected.product_id);
  const endpoint = doc.endpoints[selectedEndpoint] ?? doc.endpoints[0];

  const codeSnippet = `const response = await fetch(
  '${doc.base_url}${endpoint.path.replace('{id}', 'req_abc123')}',
  {
    method: '${endpoint.method}',
    headers: {
      'Authorization': 'Bearer sk_live_YOUR_API_KEY',
      'Content-Type': 'application/json',
    },${endpoint.method !== 'GET' && endpoint.body ? `
    body: JSON.stringify({
${endpoint.body.filter(b => b.required).map(b => `      ${b.name}: ${b.type === 'number' ? '9900' : `'value'`}`).join(',\n')}
    }),` : ''}
  }
);
const data = await response.json();`;

  const copyCode = () => {
    navigator.clipboard.writeText(codeSnippet);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="flex gap-5 min-h-0">
      {/* Sidebar */}
      <div className="w-56 shrink-0 space-y-2">
        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Your APIs</div>
        {activeApis.map((api, i) => (
          <button
            key={api.id}
            onClick={() => { setSelectedIdx(i); setSelectedEndpoint(0); }}
            className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-colors cursor-pointer whitespace-nowrap ${selectedIdx === i ? 'bg-sidebar text-white' : 'bg-white text-gray-600 hover:bg-surface-2 border border-surface-3'}`}
          >
            <div className="font-medium">{api.product_name}</div>
            <div className={`text-[10px] mt-0.5 ${selectedIdx === i ? 'text-white/60' : 'text-gray-400'}`}>
              {api.pricing_type.replace(/_/g, '-')} · {api.status}
            </div>
          </button>
        ))}

        <div className="mt-4 pt-4 border-t border-surface-3">
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Endpoints</div>
          {doc.endpoints.map((ep, i) => (
            <button
              key={i}
              onClick={() => setSelectedEndpoint(i)}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs mb-1 transition-colors cursor-pointer whitespace-nowrap ${selectedEndpoint === i ? 'bg-brand-50 text-brand-600' : 'text-gray-500 hover:bg-surface-2'}`}
            >
              <span className={`text-[10px] font-bold mr-1.5 ${methodColor[ep.method].split(' ')[1]}`}>{ep.method}</span>
              <span className="font-mono">{ep.path.length > 16 ? ep.path.slice(0, 16) + '…' : ep.path}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <motion.div key={`${selectedIdx}-${selectedEndpoint}`} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="flex-1 space-y-4">
        <div className="bg-white rounded-xl p-5">
          <div className="flex items-start gap-3 mb-4">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-md ${methodColor[endpoint.method]}`}>{endpoint.method}</span>
            <div>
              <code className="text-sm font-mono text-gray-900">{doc.base_url}{endpoint.path}</code>
              <p className="text-xs text-gray-500 mt-1">{endpoint.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[11px] text-gray-400 border-t border-surface-3 pt-3">
            <span><i className="ri-git-branch-line mr-1" />Version: {doc.version}</span>
            <span><i className="ri-lock-line mr-1" />Bearer Auth Required</span>
            <span><i className="ri-time-line mr-1" />Rate limit: 100 req/s</span>
          </div>
        </div>

        {(endpoint.params?.length || endpoint.body?.length) ? (
          <div className="bg-white rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">{endpoint.params?.length ? 'Parameters' : 'Request Body'}</h4>
            <div className="space-y-0">
              {(endpoint.params || endpoint.body || []).map((param) => (
                <div key={param.name} className="flex items-start gap-3 py-3 border-b border-surface-3 last:border-0">
                  <div className="w-36 shrink-0">
                    <code className="text-xs font-mono text-gray-800">{param.name}</code>
                    {param.required && <span className="text-[10px] text-red-500 ml-1.5">required</span>}
                  </div>
                  <span className="text-[11px] bg-surface-2 px-2 py-0.5 rounded text-gray-500 font-mono shrink-0">{param.type}</span>
                  <span className="text-xs text-gray-500">{param.description}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="bg-white rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">Code Example (Node.js)</h4>
            <button onClick={copyCode} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 cursor-pointer whitespace-nowrap transition-colors">
              <i className={copiedCode ? 'ri-check-line text-green-500' : 'ri-file-copy-line'} />
              {copiedCode ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre className="bg-gray-900 rounded-lg p-4 text-xs text-emerald-400 font-mono overflow-x-auto leading-relaxed whitespace-pre-wrap">{codeSnippet}</pre>
        </div>

        <div className="bg-white rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Example Response</h4>
          <pre className="bg-surface-2 rounded-lg p-4 text-xs text-gray-700 font-mono overflow-x-auto leading-relaxed whitespace-pre">{endpoint.response_example}</pre>
        </div>
      </motion.div>
    </div>
  );
}
